import React from 'react'
import { Box, ButtonBase, Tooltip } from '@mui/material'

import { dsColorNew } from '@palette'
import { CMP_SHORT, CMP_WORD, deltaOf, money, signedPct, thinTitle } from '../@services/format'


export const toneSx = {
	down: { color: dsColorNew.successText },
	up: { color: dsColorNew.dangerRed },
	eq: { color: dsColorNew.textMuted },
	none: { color: dsColorNew.textSubtle },
}

/** The change of a cell: ▼ −6.2% −€9 (green: cheaper / went down), ▲ +4.2% +€6 (red), = no change, no comparison */
export function Delta({ cell, code, sx = {} }) {
	const d = deltaOf(cell, code)
	if (d.kind === 'none') return <Box component="span" title={d.title} sx={{ fontSize: 11, fontWeight: 500, ...toneSx.none, ...sx }}>{d.text}</Box>
	if (d.kind === 'eq') return <Box component="span" sx={{ fontSize: 11, fontWeight: 600, ...toneSx.eq, ...sx }}>{d.text}</Box>
	return <Box component="span" sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '-0.1px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', ...toneSx[d.kind], ...sx }}>
		{d.arrow} {d.pct} <Box component="i" sx={{ fontStyle: 'normal', fontWeight: 500, opacity: 0.85 }}>{d.amount}</Box>
	</Box>
}

/**
 * One cell of the grid (CA 12, 14): price, change in % and €, the amber "!" when few prices are behind it, the green
 * ring when it is the cheapest of the date, and the previous price with "Show last year's price". A button: Enter
 * opens the detail.
 */
export default function Cell({ cell, rowName, columnLabel, cmp, code, showPrev, cheapest, onOpen }) {
	if (!cell?.cur) return <Box title="No prices for this date" sx={{ minWidth: 80, minHeight: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: dsColorNew.textSubtle }}>—</Box>

	const thin = cell.thin ? ` · few prices behind: ${cell.cur.n} now, ${cell.prev ? cell.prev.n : 0} ${CMP_WORD[cmp]}` : ''
	const aria = `${rowName}, ${columnLabel}: ${money(cell.val, code)}${cell.prev ? `, ${signedPct(cell.dp)} ${CMP_WORD[cmp]}` : ', no comparison'}${thin}. Open details`

	const button = <ButtonBase
		aria-label={aria}
		onClick={onOpen}
		sx={{
			width: '100%', minWidth: 80, minHeight: 46, px: 0.5, py: 0.5, borderRadius: '8px', position: 'relative',
			display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1px',
			border: '1px solid transparent', color: dsColorNew.textTitle,
			...(cheapest && { borderColor: 'rgba(21,128,61,.45)', backgroundColor: 'rgba(21,128,61,.06)' }),
			'&:hover': { borderColor: dsColorNew.appBlue, backgroundColor: dsColorNew.white },
			'&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.webBlue500}`, outlineOffset: 1 },
		}}
	>
		{cell.thin && <Box component="span" aria-hidden sx={{
			position: 'absolute', top: 3, right: 4, width: 15, height: 15, borderRadius: '50%',
			backgroundColor: dsColorNew.noteAmberBg, border: `1px solid ${dsColorNew.noteAmberBorder}`, color: dsColorNew.noteAmber,
			fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
		}}>!</Box>}
		<Box component="span" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{money(cell.val, code)}</Box>
		<Delta cell={cell} code={code} sx={cell.thin ? { opacity: 0.5 } : {}}/>
		{showPrev && !!cell.prev && <Box component="span" sx={{ fontSize: 11, color: dsColorNew.textMuted, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
			{CMP_SHORT[cmp]} {money(cell.prevVal, code)}
		</Box>}
	</ButtonBase>

	return cell.thin ? <Tooltip title={thinTitle(cell, cmp)} arrow>{button}</Tooltip> : button
}
